import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Header from '../../components/ui/Header';
import PropertyGallery from './components/PropertyGallery';
import PropertyHeader from './components/PropertyHeader';
import PropertySpecifications from './components/PropertySpecifications';
import NeighborhoodContext from './components/NeighborhoodContext';
import FinancialTools from './components/FinancialTools';
import OwnerContact from './components/OwnerContact';
import UserReviews from './components/UserReviews';
import SocialSharing from './components/SocialSharing';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const PropertyDetailPage = () => {
  const { id } = useParams();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showStickyContact, setShowStickyContact] = useState(false);

  // Mock property data
  const mockProperty = {
    id: "prop-001",
    title: "Luxury 3BHK Apartment with Modern Amenities",
    location: "Koramangala 4th Block, Bangalore",
    price: 8500000,
    type: "sale",
    configuration: "3BHK",
    area: 1450,
    yearBuilt: 2019,
    status: "Available",
    pricePerSqFt: 5862,
    ownerVerified: true,
    images: [
      "https://images.pexels.com/photos/1643383/pexels-photo-1643383.jpeg",
      "https://images.pexels.com/photos/2724749/pexels-photo-2724749.jpeg",
      "https://images.pexels.com/photos/1571460/pexels-photo-1571460.jpeg",
      "https://images.pexels.com/photos/2724748/pexels-photo-2724748.jpeg",
      "https://images.pexels.com/photos/1571453/pexels-photo-1571453.jpeg",
      "https://images.pexels.com/photos/2724751/pexels-photo-2724751.jpeg"
    ],
    virtualTour: "https://example.com/virtual-tour",
    videoWalkthrough: "https://example.com/video-tour.mp4",
    specifications: {
      basicDetails: {
        propertyType: "Apartment",
        bhkType: "3BHK",
        builtUpArea: "1450 sq ft",
        carpetArea: "1200 sq ft",
        floorNumber: "12th Floor",
        totalFloors: "25 Floors",
        facing: "East",
        furnishing: "Semi-Furnished"
      },
      amenitiesDetails: {
        parking: "1 Covered Parking",
        balconies: "2 Balconies",
        bathrooms: "3 Bathrooms",
        waterSupply: "24x7 Water Supply",
        powerBackup: "Full Power Backup",
        elevator: "2 High-Speed Elevators"
      },
      additionalDetails: {
        ageOfProperty: "4 Years",
        flooring: "Vitrified Tiles",
        petPolicy: "Pets Allowed",
        possession: "Ready to Move",
        maintenance: "₹3,500/month",
        securityDeposit: "₹1,70,000"
      }
    },
    floorPlan: "https://images.pexels.com/photos/8293778/pexels-photo-8293778.jpeg",
    amenities: {
      building: [
        "Swimming Pool",
        "Gym",
        "Security",
        "Elevator",
        "Power Backup",
        "Water Supply",
        "Parking",
        "Garden",
        "Club House",
        "Children Play Area",
        "Jogging Track",
        "CCTV"
      ],
      nearby: [
        "Metro Station",
        "Shopping Mall",
        "Hospital",
        "School",
        "Restaurant",
        "Bank",
        "Pharmacy",
        "Supermarket"
      ]
    }
  };

  const mockOwner = {
    name: "Rajesh Kumar",
    type: "Individual Owner",
    phone: "+91 98765 43210",
    email: "rajesh.kumar@email.com",
    avatar: "https://randomuser.me/api/portraits/men/32.jpg",
    verified: true,
    rating: 4.8,
    propertiesListed: 3,
    joinedDate: "Jan 2022",
    availability: "9 AM - 8 PM",
    bestTimeToCall: "10 AM - 6 PM",
    preferredContact: "WhatsApp",
    languages: ["English", "Hindi", "Kannada"]
  };

  const mockNeighborhood = {
    coordinates: {
      lat: 12.9352,
      lng: 77.6245
    },
    nearbyAmenities: [
      {
        name: "Forum Mall",
        type: "Shopping Mall",
        category: "shopping",
        distance: 0.8,
        walkTime: "10 min"
      },
      {
        name: "Koramangala Metro Station",
        type: "Metro Station",
        category: "transport",
        distance: 1.2,
        walkTime: "15 min"
      },
      {
        name: "Manipal Hospital",
        type: "Multi-specialty Hospital",
        category: "healthcare",
        distance: 1.5,
        walkTime: "18 min"
      },
      {
        name: "Delhi Public School",
        type: "CBSE School",
        category: "education",
        distance: 0.6,
        walkTime: "8 min"
      },
      {
        name: "Toit Brewpub",
        type: "Restaurant & Bar",
        category: "dining",
        distance: 0.4,
        walkTime: "5 min"
      },
      {
        name: "PVR Cinemas",
        type: "Movie Theater",
        category: "entertainment",
        distance: 0.9,
        walkTime: "12 min"
      }
    ],
    commuteOptions: [
      {
        destination: "Electronic City",
        distance: 12,
        options: [
          { mode: "car", time: "35 min", cost: "180" },
          { mode: "bus", time: "55 min", cost: "25" },
          { mode: "metro", time: "45 min", cost: "35" }
        ]
      },
      {
        destination: "Whitefield",
        distance: 18,
        options: [
          { mode: "car", time: "50 min", cost: "250" },
          { mode: "bus", time: "75 min", cost: "40" },
          { mode: "metro", time: "65 min", cost: "45" }
        ]
      }
    ],
    walkScore: 85,
    safetyRating: 4.2,
    noiseLevel: 3.1
  };

  const mockSimilarProperties = [
    {
      title: "Modern 3BHK in HSR Layout",
      location: "HSR Layout Sector 2",
      price: 7800000,
      configuration: "3BHK",
      area: 1380,
      pricePerSqFt: 5652
    },
    {
      title: "Spacious 3BHK in BTM Layout",
      location: "BTM Layout 2nd Stage",
      price: 9200000,
      configuration: "3BHK",
      area: 1520,
      pricePerSqFt: 6053
    },
    {
      title: "Premium 3BHK in Jayanagar",
      location: "Jayanagar 4th Block",
      price: 8900000,
      configuration: "3BHK",
      area: 1400,
      pricePerSqFt: 6357
    }
  ];

  const mockReviews = [
    {
      author: "Priya Sharma",
      avatar: "https://randomuser.me/api/portraits/women/44.jpg",
      rating: 5,
      title: "Excellent property with great amenities",
      comment: `I've been living here for 2 years and it's been a wonderful experience. The apartment is spacious, well-ventilated, and the amenities are top-notch. The swimming pool and gym are well-maintained. The location is perfect with easy access to malls, restaurants, and the metro station.`,
      date: "2024-07-10",
      category: "property",
      helpful: 12
    },
    {
      author: "Amit Patel",
      avatar: "https://randomuser.me/api/portraits/men/22.jpg",
      rating: 4,
      title: "Great neighborhood, very convenient",
      comment: `The neighborhood is fantastic! Everything you need is within walking distance. The area is safe, well-connected, and has a good mix of restaurants and shopping options. The only downside is the traffic during peak hours, but that's common in Bangalore.`,
      date: "2024-07-05",
      category: "neighborhood",
      helpful: 8
    },
    {
      author: "Sneha Reddy",
      avatar: "https://randomuser.me/api/portraits/women/31.jpg",
      rating: 5,
      title: "Responsive and helpful owner",
      comment: `Rajesh sir is very responsive and helpful. He addressed all our concerns promptly and the handover process was smooth. The property was exactly as described and shown in the photos. Highly recommend!`,
      date: "2024-06-28",
      category: "owner",
      helpful: 15
    },
    {
      author: "Karthik Nair",
      avatar: "https://randomuser.me/api/portraits/men/18.jpg",
      rating: 4,
      title: "Good amenities, well-maintained",
      comment: `The building amenities are excellent and well-maintained. The gym has modern equipment, the swimming pool is clean, and the security is good. The children's play area is also well-designed. Parking can be a bit tight during peak hours.`,
      date: "2024-06-20",
      category: "amenities",
      helpful: 6
    }
  ];

  const mockNeighborhoodRating = {
    overall: 4.3,
    breakdown: {
      safety: 4.5,
      transport: 4.2,
      lifestyle: 4.4,
      value: 4.1
    }
  };

  useEffect(() => {
    // Simulate API call
    const fetchProperty = async () => {
      setLoading(true);
      // Simulate network delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      setProperty(mockProperty);
      setLoading(false);
    };

    fetchProperty();
  }, [id]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setShowStickyContact(scrollPosition > 800);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16 flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <div className="text-text-secondary">Loading property details...</div>
          </div>
        </div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16 flex items-center justify-center min-h-screen">
          <div className="text-center">
            <Icon name="Home" size={48} className="mx-auto mb-4 text-muted-foreground" />
            <div className="text-text-primary font-semibold mb-2">Property Not Found</div>
            <div className="text-text-secondary">The property you're looking for doesn't exist.</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Property Gallery */}
          <div className="mb-8">
            <PropertyGallery
              images={property.images}
              virtualTour={property.virtualTour}
              videoWalkthrough={property.videoWalkthrough}
            />
          </div>

          {/* Property Header */}
          <div className="mb-8">
            <PropertyHeader property={property} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Property Specifications */}
              <PropertySpecifications
                specifications={property.specifications}
                floorPlan={property.floorPlan}
                amenities={property.amenities}
              />

              {/* Neighborhood Context */}
              <NeighborhoodContext neighborhood={mockNeighborhood} />

              {/* Financial Tools */}
              <FinancialTools
                property={property}
                similarProperties={mockSimilarProperties}
              />

              {/* User Reviews */}
              <UserReviews
                reviews={mockReviews}
                neighborhoodRating={mockNeighborhoodRating}
              />
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1 space-y-8">
              {/* Owner Contact */}
              <OwnerContact owner={mockOwner} property={property} />

              {/* Social Sharing */}
              <div className="bg-surface p-6 rounded-lg shadow-property-card">
                <h3 className="text-lg font-semibold text-text-primary mb-4">
                  Share This Property
                </h3>
                <SocialSharing property={property} />
              </div>

              {/* Quick Stats */}
              <div className="bg-surface p-6 rounded-lg shadow-property-card">
                <h3 className="text-lg font-semibold text-text-primary mb-4">
                  Property Stats
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <Icon name="Eye" size={16} className="text-text-secondary" />
                      <span className="text-text-secondary">Views Today</span>
                    </div>
                    <span className="font-medium text-text-primary">47</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <Icon name="Heart" size={16} className="text-text-secondary" />
                      <span className="text-text-secondary">Saved by</span>
                    </div>
                    <span className="font-medium text-text-primary">23 users</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <Icon name="MessageCircle" size={16} className="text-text-secondary" />
                      <span className="text-text-secondary">Inquiries</span>
                    </div>
                    <span className="font-medium text-text-primary">8 this week</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <Icon name="Calendar" size={16} className="text-text-secondary" />
                      <span className="text-text-secondary">Listed</span>
                    </div>
                    <span className="font-medium text-text-primary">5 days ago</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Sticky Contact Bar (Mobile) */}
      {showStickyContact && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-surface border-t border-border p-4 lg:hidden">
          <div className="flex gap-3">
            <Button
              variant="outline"
              iconName="Phone"
              iconPosition="left"
              onClick={() => window.location.href = `tel:${mockOwner.phone}`}
              className="flex-1"
            >
              Call
            </Button>
            <Button
              variant="secondary"
              iconName="MessageCircle"
              iconPosition="left"
              onClick={() => {
                const message = `Hi, I'm interested in your property: ${property.title}`;
                const whatsappUrl = `https://wa.me/${mockOwner.phone.replace(/\D/g, '')}?text=${encodeURIComponent(message)}`;
                window.open(whatsappUrl, '_blank');
              }}
              className="flex-1"
            >
              WhatsApp
            </Button>
            <Button
              variant="default"
              iconName="Calendar"
              iconPosition="left"
              className="flex-1"
            >
              Visit
            </Button>
          </div>
        </div>
      )}

      {/* Footer Spacing for Sticky Bar */}
      <div className="h-20 lg:h-0"></div>
    </div>
  );
};

export default PropertyDetailPage;